/**
 * usePortalRedirect
 *
 * Subdomain-aware routing hook. Enforces that:
 * - admin.bimanova.com  → only /admin/* routes are accessible
 * - app.bimanova.com    → only tenant (non-admin) routes are accessible
 *
 * In development (localhost), both portals are accessible without redirect.
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const ADMIN_SUBDOMAIN = 'admin';

const getSubdomain = (): string | null => {
  const hostname = window.location.hostname;

  // Skip subdomain detection for localhost / dev environments
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')) {
    return null;
  }

  // Extract subdomain: "admin.bimanova.com" → "admin"
  const parts = hostname.split('.');
  if (parts.length >= 3) {
    return parts[0]!;
  }

  return null;
};

export const usePortalRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const subdomain = getSubdomain();
    if (!subdomain) return; // localhost — no enforcement

    const isAdminPath = location.pathname.startsWith('/admin');

    if (subdomain === ADMIN_SUBDOMAIN && !isAdminPath) {
      // On admin.bimanova.com but accessing tenant routes → redirect to /admin
      // Allow auth routes (/login, /forgot-password, etc.) on both
      if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/forgot') && !location.pathname.startsWith('/reset') && location.pathname !== '/') {
        navigate('/admin', { replace: true });
      } else if (location.pathname === '/') {
        navigate('/admin', { replace: true });
      }
    } else if (subdomain !== ADMIN_SUBDOMAIN && isAdminPath) {
      // On app.bimanova.com (or any non-admin subdomain) but accessing /admin → redirect to /
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);
};

export default usePortalRedirect;
